import type { WebGLRenderer } from 'three';
import { DEBUG_ENABLED } from './DebugFlags';
import { debugDock, makeDraggable } from './DebugDock';

/** Loose snapshot of per-frame game state captured alongside hitches. */
export interface PerfSnapshot {
  [key: string]: unknown;
}

/** One completed frame in the ring buffer. */
export interface PerfFrame {
  /** Wall-clock frame time in milliseconds (unclamped). */
  dt: number;
  updateMs: number;
  renderMs: number;
  /** Section timings (ms), keyed by the `mark()` label. */
  labels: Record<string, number>;
  calls: number;
  triangles: number;
}

/** A frame that exceeded the hitch threshold. */
export interface PerfHitch {
  t: number;
  dt: number;
  updateMs: number;
  renderMs: number;
  labels: Record<string, number>;
  snapshot?: PerfSnapshot;
  /** Linked shader programs at hitch time (compile hitches) when available. */
  programs?: number;
}

/** Public surface exposed as `window.__THREE_GAME_PERF__`. */
export interface ThreeGamePerf {
  fps: number;
  frameMs: number;
  p50: number;
  p99: number;
  hitches: PerfHitch[];
  current: PerfFrame | null;
}

declare global {
  interface Window {
    __THREE_GAME_PERF__?: ThreeGamePerf;
  }
}

const RING_SIZE = 300;
const MAX_HITCHES = 50;
const VISIBLE_HITCHES = 5;
const UI_UPDATE_MS = 250;
const DEFAULT_HITCH_MS = 33;

/** Section labels, in frame-timeline order, used for hitch attribution. */
const SECTION_LABELS = [
  'ocean',
  'sky',
  'atmosphere',
  'input',
  'visuals',
  'streamer',
  'wake',
  'foam',
  'combatFx',
  'audio',
  'diagnostics',
  'postFx',
] as const;

function createEmptyLabels(): Record<string, number> {
  const labels: Record<string, number> = {};
  for (const label of SECTION_LABELS) labels[label] = 0;
  return labels;
}

function formatTriangles(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return `${value}`;
}

export class PerfOverlay {
  private readonly renderer: WebGLRenderer;
  private readonly hitchMs: number;
  private readonly enabledFlag: boolean;

  private readonly ring: PerfFrame[] = new Array<PerfFrame>(RING_SIZE);
  private ringIndex = -1;
  private ringCount = 0;

  private current: PerfFrame | null = null;
  private frameStart = 0;
  private updateMarkTime = 0;
  private lastMark = 0;
  private prevLabel: string | null = null;

  private readonly hitches: PerfHitch[] = [];
  private readonly exposed: ThreeGamePerf = {
    fps: 0,
    frameMs: 0,
    p50: 0,
    p99: 0,
    hitches: this.hitches,
    current: null,
  };

  private visible = true;
  private dom: HTMLElement | null = null;
  private statsEl: HTMLElement | null = null;
  private hitchesEl: HTMLElement | null = null;
  private lastUiUpdate = 0;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.code !== 'Backquote') return;
    debugDock.toggle();
  };

  constructor(
    renderer: WebGLRenderer,
    options?: { enabled?: boolean; hitchMs?: number },
  ) {
    this.renderer = renderer;
    this.hitchMs = options?.hitchMs ?? DEFAULT_HITCH_MS;
    this.enabledFlag = options?.enabled ?? true;

    for (let i = 0; i < RING_SIZE; i += 1) {
      this.ring[i] = {
        dt: 0,
        updateMs: 0,
        renderMs: 0,
        labels: createEmptyLabels(),
        calls: 0,
        triangles: 0,
      };
    }

    if (typeof window !== 'undefined') {
      window.__THREE_GAME_PERF__ = this.exposed;
      window.addEventListener('keydown', this.onKeyDown);
      if (DEBUG_ENABLED) {
        console.info('[perf] diagnostics overlay enabled — press ` to toggle');
      }
    }
  }

  get enabled(): boolean {
    return this.enabledFlag && DEBUG_ENABLED;
  }

  beginFrame(deltaSeconds: number): void {
    if (!this.enabled) return;
    const now = performance.now();
    this.ringIndex = (this.ringIndex + 1) % RING_SIZE;
    if (this.ringCount < RING_SIZE) this.ringCount += 1;

    const slot = this.ring[this.ringIndex];
    const labels = slot.labels;
    for (const label of SECTION_LABELS) labels[label] = 0;
    slot.dt = deltaSeconds * 1000;
    slot.updateMs = 0;
    slot.renderMs = 0;
    slot.calls = 0;
    slot.triangles = 0;

    this.current = slot;
    this.frameStart = now;
    this.updateMarkTime = now;
    this.lastMark = now;
    this.prevLabel = null;
  }

  mark(label: string): void {
    if (!this.enabled) return;
    const slot = this.current;
    if (!slot) return;
    const now = performance.now();
    if (
      this.prevLabel !== null &&
      this.prevLabel !== 'update' &&
      this.prevLabel !== 'render'
    ) {
      slot.labels[this.prevLabel] =
        (slot.labels[this.prevLabel] ?? 0) + (now - this.lastMark);
    }
    if (label === 'update') {
      this.updateMarkTime = now;
      slot.updateMs = now - this.frameStart;
    } else if (label === 'render') {
      slot.renderMs = now - this.updateMarkTime;
    }
    this.prevLabel = label;
    this.lastMark = now;
  }

  endFrame(snapshot?: PerfSnapshot): void {
    if (!this.enabled) return;
    const slot = this.current;
    if (!slot) return;
    this.ensureDom();

    const now = performance.now();
    if (
      this.prevLabel !== null &&
      this.prevLabel !== 'update' &&
      this.prevLabel !== 'render'
    ) {
      slot.labels[this.prevLabel] =
        (slot.labels[this.prevLabel] ?? 0) + (now - this.lastMark);
    }
    this.prevLabel = null;
    if (slot.renderMs === 0) slot.renderMs = now - this.updateMarkTime;

    const info = this.renderer.info;
    slot.calls = info.render.calls;
    slot.triangles = info.render.triangles;

    if (slot.dt > this.hitchMs) this.recordHitch(slot, snapshot, now);

    this.exposed.current = slot;
    this.exposed.frameMs = slot.dt;
    if (now - this.lastUiUpdate >= UI_UPDATE_MS) {
      this.lastUiUpdate = now;
      this.recomputeStats();
      this.updateDom();
    }
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    if (visible) {
      this.ensureDom();
      if (this.dom) this.dom.hidden = false;
    } else if (this.dom) {
      this.dom.hidden = true;
    }
  }

  dispose(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onKeyDown);
      if (window.__THREE_GAME_PERF__ === this.exposed) {
        window.__THREE_GAME_PERF__ = undefined;
      }
    }
    this.dom?.remove();
    this.dom = null;
    this.statsEl = null;
    this.hitchesEl = null;
    this.current = null;
  }

  private recordHitch(slot: PerfFrame, snapshot: PerfSnapshot | undefined, now: number): void {
    const hitch: PerfHitch = {
      t: now,
      dt: slot.dt,
      updateMs: slot.updateMs,
      renderMs: slot.renderMs,
      labels: { ...slot.labels },
      snapshot,
    };
    const programs = this.renderer.info.programs;
    if (programs) hitch.programs = programs.length;
    this.hitches.unshift(hitch);
    if (this.hitches.length > MAX_HITCHES) this.hitches.length = MAX_HITCHES;
  }

  private recomputeStats(): void {
    const n = this.ringCount;
    if (n === 0) return;
    const dts = new Array<number>(n);
    let sum = 0;
    for (let i = 0; i < n; i += 1) {
      const dt = this.ring[i].dt;
      dts[i] = dt;
      sum += dt;
    }
    dts.sort((a, b) => a - b);
    this.exposed.p50 = dts[Math.max(0, Math.ceil(n * 0.5) - 1)];
    this.exposed.p99 = dts[Math.max(0, Math.ceil(n * 0.99) - 1)];
    this.exposed.fps = sum > 0 ? 1000 / (sum / n) : 0;
  }

  private ensureDom(): void {
    if (this.dom || typeof document === 'undefined') return;
    const root = document.getElementById('perf-overlay') ?? this.createRoot();
    this.dom = root;
    this.statsEl = root.querySelector<HTMLElement>('#perf-stats') ?? this.createChild(root, 'perf-stats');
    this.hitchesEl = root.querySelector<HTMLElement>('#perf-hitches') ?? this.createChild(root, 'perf-hitches');
    const title = root.querySelector<HTMLElement>('#perf-title');
    // Whole-panel drag: this read-only panel has no interactive children, so any
    // pointerdown on it can start a move (the title row is the visual grip).
    if (title) title.style.cursor = 'move';
    makeDraggable(root);
    debugDock.register(root);
    root.hidden = !this.visible;
  }

  private createRoot(): HTMLElement {
    const root = document.createElement('div');
    root.id = 'perf-overlay';
    document.body.appendChild(root);
    return root;
  }

  private createChild(root: HTMLElement, id: string): HTMLElement {
    const el = document.createElement('div');
    el.id = id;
    root.appendChild(el);
    return el;
  }

  private updateDom(): void {
    if (!this.statsEl || !this.hitchesEl) return;
    const current = this.exposed.current;
    const calls = current?.calls ?? 0;
    const triangles = current?.triangles ?? 0;
    const jsMs = (current?.updateMs ?? 0) + (current?.renderMs ?? 0);
    this.statsEl.textContent =
      `FPS ${this.exposed.fps.toFixed(0)} · ` +
      `frame ${this.exposed.frameMs.toFixed(1)} / ${this.exposed.p50.toFixed(1)} / ${this.exposed.p99.toFixed(1)} ms · ` +
      `JS ${jsMs.toFixed(1)} ms · ${calls} calls · ${formatTriangles(triangles)} tris`;

    const lines: string[] = [];
    const limit = Math.min(VISIBLE_HITCHES, this.hitches.length);
    for (let i = 0; i < limit; i += 1) {
      lines.push(this.formatHitch(this.hitches[i]));
    }
    this.hitchesEl.textContent = lines.join('\n');
  }

  private formatHitch(hitch: PerfHitch): string {
    const parts: string[] = [
      `dt ${hitch.dt.toFixed(0)}ms`,
      `update ${hitch.updateMs.toFixed(1)}`,
      `render ${hitch.renderMs.toFixed(1)}`,
    ];
    const ranked = SECTION_LABELS
      .filter((label) => (hitch.labels[label] ?? 0) > 0)
      .sort((a, b) => (hitch.labels[b] ?? 0) - (hitch.labels[a] ?? 0))
      .slice(0, 3);
    for (const label of ranked) {
      parts.push(`${label} ${(hitch.labels[label] ?? 0).toFixed(1)}`);
    }
    if (hitch.programs !== undefined) parts.push(`prog ${hitch.programs}`);
    return parts.join('  ');
  }
}
